import React, { useContext, useEffect, useRef } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';
import FormFieldContext from '../FormFieldContext';

/*
 * AddressLookupField.tsx
 * Watches the postal code and house number fields of an address form. When
 * both are filled, the address is looked up at the postcode api and the
 * street, city and country fields of the form are filled with the result.
 * Renders nothing, an unknown address simply leaves the fields untouched.
 */

// Dutch postal code format after removing whitespace, for example 1234AB
const POSTAL_CODE_PATTERN = /^[0-9]{4}[A-Z]{2}$/;

// Delay before the lookup starts after typing
const LOOKUP_DEBOUNCE_MS = 400;

const AddressLookupField: React.FC<{ viewKey: string }> = ({ viewKey }) => {
    const { setUnsavedChanges } = useGlobalContext();
    const { handleLogout } = useAuthContext();
    const { updatedData, setUpdatedData } = useContext(FormFieldContext);
    const lastLookupRef = useRef<string | null>(null);
    const requestIdRef = useRef(0);

    // Get the postal code and house number which are typed in the form fields
    const postalCode = typeof updatedData?.postal_code === 'string' ? updatedData.postal_code : '';
    const houseNumber = typeof updatedData?.house_number === 'string' ? updatedData.house_number : '';

    // Normalizes the postal code by removing whitespace and uppercasing it
    const normalizePostalCode = (value: string) => value.replace(/\s+/g, '').toUpperCase();

    // Determines if the entered values are complete enough to look up
    const canLookup = (postalCodeValue: string, houseNumberValue: string) => {
        return POSTAL_CODE_PATTERN.test(normalizePostalCode(postalCodeValue)) && /^\s*\d+/.test(houseNumberValue);
    };

    // Looks up the address and fills the street, city and country fields
    const lookupAddress = async (postalCodeValue: string, houseNumberValue: string) => {
        const normalizedPostalCode = normalizePostalCode(postalCodeValue);
        const trimmedHouseNumber = houseNumberValue.trim();
        const lookupKey = `${normalizedPostalCode}/${trimmedHouseNumber}`;

        // Skip the lookup when the same values were looked up already
        if (lastLookupRef.current === lookupKey) return;
        lastLookupRef.current = lookupKey;

        // Keep track of the request, so an older response cannot overwrite a newer one
        const requestId = ++requestIdRef.current;

        try {
            // Fetch the address from the lookup endpoint
            const address = await fetchData({
                apiUrl: 'get_address_lookup',
                params: { postal_code: normalizedPostalCode, house_number: trimmedHouseNumber },
                handleLogout,
            });

            // Ignore the response when a newer lookup was started
            if (requestId !== requestIdRef.current) return;

            // Fill the street, city and country fields with the found address
            if (address && address.street) {
                setUpdatedData((prev: Record<string, any>) => ({
                    ...prev,
                    street: address.street,
                    city: address.city,
                    country: prev.country || address.country,
                }));
                setUnsavedChanges(viewKey, true);
            }
        } catch {
            // An unknown or unavailable address leaves the fields untouched
        }
    };

    // Starts the lookup after a short delay when both fields are filled
    useEffect(() => {
        if (!canLookup(postalCode, houseNumber)) return;

        // Debounce the lookup while typing
        const timeout = setTimeout(() => {
            lookupAddress(postalCode, houseNumber);
        }, LOOKUP_DEBOUNCE_MS);

        return () => clearTimeout(timeout);
    }, [postalCode, houseNumber]);

    // The lookup has no visible output
    return null;
};

export default AddressLookupField;
